import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { flatten } from 'lodash';

import { CallNumbersDto, CallNumbersItemDto } from '@app/components';
import { usePaging, useQueryParams } from '@app/hooks';
import { escapeForbiddenSymbolsInQuery, makeRequest } from '@app/utils';
import { defaultRecordsPerPage, Resource } from '@app/constants';

export const useCallNumbersFetch = (
  pageNumber: number,
  searchQuery: string
): Resource<CallNumbersItemDto, CallNumbersDto> => {
  const [pages, setPages] = useState<CallNumbersItemDto[][]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const queryParams = useQueryParams();

  const callNumbersQuery = useQuery(
    ['callNumbersPage', searchQuery, pageNumber],
    () => {
      const escapedQuery = escapeForbiddenSymbolsInQuery(searchQuery);
      const isSearchedQuery = searchQuery === queryParams.search;
      const highlightQuery = `callNumber>="${escapedQuery}" or callNumber<"${escapedQuery}"`;
      const paginationQuery = `callNumber>"${escapedQuery}"`;

      return makeRequest<CallNumbersDto>({
        url: '/browse/call-numbers/instances',
        params: {
          highlightMatch: true,
          limit: defaultRecordsPerPage,
          precedingRecordsCount: 4,
          query: isSearchedQuery ? highlightQuery : paginationQuery,
        },
      });
    },
    { enabled: Boolean(searchQuery) }
  );

  useEffect(() => {
    if (callNumbersQuery.data?.totalRecords !== undefined) {
      const hasNextPage = callNumbersQuery.data?.next;

      if (hasNextPage) {
        setTotalRecords(callNumbersQuery.data.totalRecords);
      } else {
        setTotalRecords(flatten(pages).length);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pages]);

  usePaging<CallNumbersItemDto>({
    areRecordsFetched: callNumbersQuery.isSuccess,
    records: callNumbersQuery.data?.items,
    isNewPageRequest: pageNumber !== 1,
    pageNumber,
    setPages,
  });

  return {
    pages,
    recordKey: 'shelfKey',
    totalRecords,
    queryResult: callNumbersQuery,
    next: callNumbersQuery.data?.next,
  };
};
